import { ReactElement, useMemo } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, Card, Col, Descriptions, Flex, Row, Space, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { LenderDetailsProps } from '../interfaces';
import { getComplianceItems, formatAddress } from '../utils';
import { LendersGetLenderResponse } from '@pmp/api';
import { Guard } from '@ohpen/react-authz';
import { DescriptionsItemType } from 'antd/es/descriptions';
import styles from '../styles.module.css';

export const LenderDetails = ({ details, isFetching, onEdit }: LenderDetailsProps): ReactElement => {
  const { t } = useTranslation();

  const generalInfoItems = useMemo(() => {
    if (!details) return [];

    return [
      { key: 'name', label: t('lender.lender.labels.name'), children: details.name || '-' },
      { key: 'acronym', label: t('lender.lender.labels.acronym'), children: details.acronym || '-' },
    ];
  }, [details, t]);

  const addressItems = useMemo(() => {
    if (!details?.address) return [];

    return [
      {
        key: 'address',
        label: t('lender.lender.labels.address'),
        children: formatAddress(details.address),
      },
    ];
  }, [details, t]);

  const firmComplianceItems: DescriptionsItemType[] = useMemo(() => {
    return getComplianceItems(details?.complianceCheckConfiguration?.firmPermissionLevelPerStatus);
  }, [details]);

  const intermediaryComplianceItems: DescriptionsItemType[] = useMemo(() => {
    return getComplianceItems(details?.complianceCheckConfiguration?.intermediaryPermissionLevelPerStatus);
  }, [details]);

  if (isFetching || !details) {
    return (
      <Flex justify="center" align="center" style={{ height: '100%' }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <Flex vertical style={{ overflowY: 'auto' }}>
      <Guard permissions={['lenders:update']} flags={['enableLenderCrud']}>
        <Flex justify="end" style={{ margin: '8px 0 16px' }}>
          <Button
            type="primary"
            aria-label="Edit exclusives"
            icon={<EditOutlined key="edit" rev={undefined} />}
            onClick={() => onEdit(details as unknown as LendersGetLenderResponse)}
            disabled={!details}
          >
            {t('global.labels.edit')}
          </Button>
        </Flex>
      </Guard>
      <Flex vertical gap={16}>
        <Row gutter={16} style={{ margin: 0 }}>
          <Col span={12} className={styles.col}>
            <Card title="General information">
              <Space direction="vertical" size="large">
                <Descriptions column={2} size="small" items={generalInfoItems} layout="vertical" />
                <Descriptions column={1} size="small" items={addressItems} layout="vertical" />
              </Space>
            </Card>
          </Col>
          <Col span={12} className={styles.col}>
            <Card title="Business development manager">
              <Descriptions column={1} size="small" layout="vertical">
                <Descriptions.Item label={t('lender.lender.labels.defaultBDM')}>
                  {details?.defaultBDM ? t(`lender.lender.labels.notAvailable${details?.defaultBDM}`) : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
        <Row gutter={16} style={{ margin: 0 }}>
          <Col span={12} className={styles.col}>
            <Card title="Firm compliance configuration">
              <Space direction="vertical" size="large">
                <Descriptions column={1} size="small" layout="vertical">
                  <Descriptions.Item label={t('lender.lender.labels.initialComplianceStatus')}>
                    {t(`lender.lender.labels.${details?.complianceCheckConfiguration.defaultFirmStatus ?? ''}`)}
                  </Descriptions.Item>
                </Descriptions>
                <Space direction="vertical" size="small">
                  <Typography.Text type="secondary" strong>
                    {t('lender.lender.labels.complianceStatusPermissions')}:
                  </Typography.Text>
                  <Descriptions column={5} size="small" items={firmComplianceItems} layout="vertical" />
                </Space>
              </Space>
            </Card>
          </Col>
          <Col span={12} className={styles.col}>
            <Card title="Intermediary compliance configuration">
              <Space direction="vertical" size="large">
                <Descriptions column={1} size="small" layout="vertical">
                  <Descriptions.Item label={t('lender.lender.labels.initialComplianceStatus')}>
                    {t(`lender.lender.labels.${details?.complianceCheckConfiguration.defaultIntermediaryStatus ?? ''}`)}
                  </Descriptions.Item>
                </Descriptions>
                <Space direction="vertical" size="small">
                  <Typography.Text type="secondary" strong>
                    {t('lender.lender.labels.complianceStatusPermissions')}:
                  </Typography.Text>
                  <Descriptions column={5} size="small" items={intermediaryComplianceItems} layout="vertical" />
                </Space>
              </Space>
            </Card>
          </Col>
        </Row>
      </Flex>
    </Flex>
  );
};
